import { CESION_ECHEQ_ACTIVITY, NEGOTIATE_ECHEQ_ACTIVITY } from "@ui-kit/components/index.es";

import { echeqConceptLabel } from "util/checks";
import * as i18n from "util/i18n";
import { getGeneralTicketItem } from "util/transaction";

import { CURRENCY_CODE_COELSA } from "../../../constants";

const FORM_ID = "echeq.transaction";

/** Resolves the PDF amount format from the COELSA currency code. */
function resolveAmountFormat(currency) {
    return currency === CURRENCY_CODE_COELSA.USD ? "amountUSD" : "amountARS";
}

/** Builds the common ECHEQ detail rows while preserving each legacy PDF order. */
function buildCommonEcheqRows(transactionData, isNegotiate = false) {
    const { echeq } = transactionData;
    const concept = `${echeq.cheque_concepto} ${echeqConceptLabel(echeq.cheque_concepto, "-")}`;

    return [
        getGeneralTicketItem(`${FORM_ID}.amount`, echeq.monto, resolveAmountFormat(echeq.moneda)),
        getGeneralTicketItem(`${FORM_ID}.emision`, echeq.fecha_emision, "date"),
        getGeneralTicketItem(`${FORM_ID}.payed`, echeq.fecha_pago, "date"),
        getGeneralTicketItem(
            `${FORM_ID}.echeq.type`,
            i18n.get(`echeqs.emit.${echeq.cheque_tipo?.toLowerCase()}.label`),
            "string",
        ),
        getGeneralTicketItem(`${FORM_ID}.echeq.character`, echeq.cheque_caracter, "string"),
        getGeneralTicketItem(`${FORM_ID}.mode`, i18n.get(`echeqs.${echeq.cheque_modo}.modo`), "string"),
        ...(isNegotiate
            ? [
                  getGeneralTicketItem(`${FORM_ID}.motive`, echeq.cheque_motivo_pago, "string"),
                  getGeneralTicketItem(`${FORM_ID}.concept`, concept, "string"),
              ]
            : [
                  getGeneralTicketItem(`${FORM_ID}.concept`, concept, "string"),
                  getGeneralTicketItem(`${FORM_ID}.motive`, echeq.cheque_motivo_pago, "string"),
              ]),
    ];
}

/** Builds the downloadable PDF payload for an ECHEQ negotiation. */
function buildNegotiateTicket(transactionData) {
    const { echeq } = transactionData;

    return {
        ticketName: i18n.get("echeqs.negotiate.title"),
        ticketBody: [
            getGeneralTicketItem("negotiate.transaction.date.label", transactionData.submitDateTime, "datefulltime"),
            getGeneralTicketItem(
                "negotiate.transaction.voucher.label",
                transactionData.idTransaction.substring(0, 8).toUpperCase(),
                "string",
            ),
            getGeneralTicketItem(`${FORM_ID}.beneficiary`, transactionData.beneficiary?.reference, "string"),
            getGeneralTicketItem(
                `${FORM_ID}.endorse`,
                i18n.get(`echeqs.${transactionData.type?.toLowerCase()}.label`),
                "string",
            ),
            getGeneralTicketItem("echeqs.account.label", transactionData.accountLabel, "string"),
            getGeneralTicketItem(`${FORM_ID}.reference`, transactionData.reference, "string"),
            getGeneralTicketItem(`${FORM_ID}.value.reference`, transactionData.referencevalue, "string"),
            getGeneralTicketItem(`${FORM_ID}.echeq.number`, echeq.cheque_numero, "string"),
            getGeneralTicketItem(`${FORM_ID}.echeq.id`, echeq.cheque_id, "string"),
            ...buildCommonEcheqRows(transactionData, true),
        ],
    };
}

/** Builds the downloadable PDF payload for an ECHEQ assignment (cesión). */
function buildCesionTicket(transactionData) {
    const { echeq } = transactionData;

    return {
        ticketName: i18n.get("echeqs.emit-cesion.title"),
        ticketBody: [
            getGeneralTicketItem(`${FORM_ID}.date.time`, transactionData.submitDateTime, "datetime"),
            getGeneralTicketItem(
                `${FORM_ID}.transaction`,
                transactionData.idTransaction.substring(0, 8).toUpperCase(),
                "string",
            ),
            getGeneralTicketItem(`${FORM_ID}.echeq.number`, echeq.cheque_numero, "string"),
            getGeneralTicketItem(`${FORM_ID}.echeq.id`, echeq.cheque_id, "string"),
            getGeneralTicketItem(
                `${FORM_ID}.cesion_date`,
                transactionData.cesion?.fecha_emision_cesion,
                "string",
            ),
            getGeneralTicketItem(`${FORM_ID}.cesion.id`, transactionData.cesion?.cesion_id, "string"),
            getGeneralTicketItem(`${FORM_ID}.beneficiary`, transactionData.beneficiary?.reference, "string"),
            getGeneralTicketItem(`${FORM_ID}.beneficiary.document`, transactionData.beneficiary?.document, "string"),
            getGeneralTicketItem(`${FORM_ID}.cesion.address`, transactionData.address, "string"),
            ...buildCommonEcheqRows(transactionData),
            getGeneralTicketItem(`${FORM_ID}.reference`, transactionData.reference || "", "string"),
            getGeneralTicketItem(`${FORM_ID}.value.reference`, transactionData.referencevalue || "", "string"),
            getGeneralTicketItem(`${FORM_ID}.coelsa.response`, transactionData.coelsaResponse, "string"),
            getGeneralTicketItem(`${FORM_ID}.coelsa.code`, transactionData.coelsaCode, "string"),
        ],
    };
}

/** Builds the downloadable PDF payload for an ECHEQ endorsement. */
function buildEndorseTicket(transactionData) {
    const { echeq } = transactionData;

    return {
        ticketName: i18n.get("echeqs.transaction.label"),
        ticketBody: [
            getGeneralTicketItem("negotiate.transaction.date.label", transactionData.creationDate, "datefulltime"),
            getGeneralTicketItem(
                `${FORM_ID}.transaction`,
                transactionData.idTransaction.substring(0, 8).toUpperCase(),
                "string",
            ),
            getGeneralTicketItem(`${FORM_ID}.echeq.number`, echeq.cheque_numero, "string"),
            getGeneralTicketItem(`${FORM_ID}.echeq.id`, echeq.cheque_id, "string"),
            getGeneralTicketItem(
                `${FORM_ID}.endorse`,
                i18n.get(`echeqs.${transactionData.type?.toLowerCase()}.label`),
                "string",
            ),
            getGeneralTicketItem("echeqs.endorse.date", transactionData.submitDateTime, "datefulltime"),
            getGeneralTicketItem(`${FORM_ID}.beneficiary`, transactionData.beneficiary?.reference, "string"),
            getGeneralTicketItem(`${FORM_ID}.beneficiary.document`, transactionData.beneficiary?.document, "string"),
            ...buildCommonEcheqRows(transactionData),
            getGeneralTicketItem(`${FORM_ID}.reference`, transactionData.reference, "string"),
            getGeneralTicketItem(`${FORM_ID}.value.reference`, transactionData.referencevalue, "string"),
            getGeneralTicketItem(`${FORM_ID}.coelsa.response`, transactionData.coelsaResponse, "string"),
            getGeneralTicketItem(`${FORM_ID}.coelsa.code`, transactionData.coelsaCode, "string"),
        ],
    };
}

/** Builds the legacy-compatible generic PDF payload for the V3 ECHEQ receipt. */
export default function buildEcheqTicketPdfData(transactionData) {
    if (transactionData.idActivity === NEGOTIATE_ECHEQ_ACTIVITY) {
        return buildNegotiateTicket(transactionData);
    }
    if (transactionData.idActivity === CESION_ECHEQ_ACTIVITY) {
        return buildCesionTicket(transactionData);
    }
    return buildEndorseTicket(transactionData);
}
